import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, BarChart3, Bot, CheckCircle2, Cpu, CreditCard, Database, FileText, Layers3, Lock, Mail, Play, Plus,
  RefreshCw, Save, ShieldCheck, Wrench, XCircle
} from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from '../ui/sonner';
import WorkflowCanvas from './agent-os/WorkflowCanvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TABS = [
  { id: 'summary', label: 'Resumen', icon: Activity },
  { id: 'workflows', label: 'Workflows', icon: Layers3 },
  { id: 'approvals', label: 'Aprobaciones', icon: ShieldCheck },
  { id: 'tools', label: 'Integraciones', icon: Cpu },
  { id: 'memory', label: 'Memoria', icon: Database },
];

const TOOL_ICONS = {
  stripe: CreditCard,
  outlook: Mail,
  publishing: Activity,
  crm: BarChart3,
  resend: Mail,
  deployments: Wrench,
  tasks: Bot,
  memory: Database,
  neuralia_skills: Layers3,
  deepseek: Cpu,
  signwell: FileText,
  linkedin: Cpu,
  tiktok: Cpu,
};

const STATUS_STYLES = {
  connected: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  internal: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

const MetricCard = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-semibold text-text-heading">{value}</p>
        {helper && <p className="mt-2 text-xs text-text-muted">{helper}</p>}
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-6 w-6" /></div>
    </div>
  </div>
);

export default function AgentOSHub({ authFetch }) {
  const [section, setSection] = useState('summary');
  const [overview, setOverview] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [tools, setTools] = useState([]);
  const [memoryEntries, setMemoryEntries] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [workflowDraft, setWorkflowDraft] = useState(null);
  const [executionContext, setExecutionContext] = useState('');
  const [approvalNotes, setApprovalNotes] = useState({});
  const [memoryForm, setMemoryForm] = useState({ title: '', category: 'aprendizaje', content: '', tags: '' });
  const [busy, setBusy] = useState(false);

  const loadOverview = async () => {
    const res = await authFetch(`${API}/admin/agent-os/overview`);
    setOverview(await res.json());
  };

  const loadWorkflows = async () => {
    const res = await authFetch(`${API}/admin/agent-os/workflows`);
    const data = await res.json();
    setWorkflows(data.workflows || []);
    if (!selectedWorkflowId && data.workflows?.[0]?.id) setSelectedWorkflowId(data.workflows[0].id);
  };

  const loadApprovals = async () => {
    const res = await authFetch(`${API}/admin/agent-os/approvals`);
    setApprovals((await res.json()).approvals || []);
  };

  const loadTools = async () => {
    const res = await authFetch(`${API}/admin/agent-os/tools`);
    setTools((await res.json()).tools || []);
  };

  const loadMemory = async () => {
    const res = await authFetch(`${API}/admin/agent-os/memory`);
    setMemoryEntries((await res.json()).entries || []);
  };

  const refreshAll = async () => {
    try {
      await Promise.all([loadOverview(), loadWorkflows(), loadApprovals(), loadTools(), loadMemory()]);
    } catch {
      toast.error('No se pudo cargar Agent OS');
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const selectedWorkflow = useMemo(() => workflows.find((item) => item.id === selectedWorkflowId) || null, [workflows, selectedWorkflowId]);

  useEffect(() => {
    setWorkflowDraft(selectedWorkflow || null);
    setSelectedNodeId(selectedWorkflow?.nodes?.[0]?.id || null);
  }, [selectedWorkflow]);

  const createFromTemplate = async (template) => {
    try {
      setBusy(true);
      const res = await authFetch(`${API}/admin/agent-os/workflows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_key: template.key,
          name: `${template.name} · Neuralia`,
          description: template.description,
          category: template.category,
          use_case: template.use_case,
          approval_mode: 'human',
          status: 'draft',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear el workflow');
      toast.success('Workflow creado desde plantilla');
      setSelectedWorkflowId(data.workflow.id);
      await refreshAll();
      setSection('workflows');
    } catch (error) {
      toast.error(error.message || 'No se pudo crear el workflow');
    } finally {
      setBusy(false);
    }
  };

  const updateWorkflowDraftField = (field, value) => setWorkflowDraft((prev) => ({ ...prev, [field]: value }));

  const updateSelectedNodeField = (field, value) => {
    setWorkflowDraft((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => {
        if (node.id !== selectedNodeId) return node;
        if (field === 'prompt' || field === 'action' || field === 'tool_key' || field === 'agent_role') {
          return { ...node, config: { ...node.config, [field]: value } };
        }
        return { ...node, [field]: value };
      }),
    }));
  };

  const saveWorkflowDraft = async () => {
    if (!workflowDraft) return;
    try {
      setBusy(true);
      const res = await authFetch(`${API}/admin/agent-os/workflows/${workflowDraft.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: workflowDraft.name,
          description: workflowDraft.description,
          category: workflowDraft.category,
          use_case: workflowDraft.use_case,
          approval_mode: workflowDraft.approval_mode,
          status: workflowDraft.status,
          nodes: workflowDraft.nodes,
          edges: workflowDraft.edges,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo guardar el workflow');
      toast.success('Workflow guardado');
      await refreshAll();
      setSelectedWorkflowId(data.workflow.id);
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar el workflow');
    } finally {
      setBusy(false);
    }
  };

  const runWorkflow = async () => {
    if (!workflowDraft) return;
    try {
      setBusy(true);
      const res = await authFetch(`${API}/admin/agent-os/workflows/${workflowDraft.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: executionContext || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo ejecutar el workflow');
      toast.success(data.pending_approval ? 'Ejecución lanzada con aprobación humana pendiente' : 'Workflow ejecutado');
      await refreshAll();
      setSection('approvals');
    } catch (error) {
      toast.error(error.message || 'No se pudo ejecutar el workflow');
    } finally {
      setBusy(false);
    }
  };

  const resolveApproval = async (approvalId, decision) => {
    try {
      setBusy(true);
      const res = await authFetch(`${API}/admin/agent-os/approvals/${approvalId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes: approvalNotes[approvalId] || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo resolver la aprobación');
      toast.success(decision === 'approved' ? 'Nodo aprobado' : 'Nodo rechazado');
      await refreshAll();
    } catch (error) {
      toast.error(error.message || 'No se pudo resolver la aprobación');
    } finally {
      setBusy(false);
    }
  };

  const createMemoryEntry = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      const res = await authFetch(`${API}/admin/agent-os/memory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: memoryForm.title,
          category: memoryForm.category,
          content: memoryForm.content,
          tags: memoryForm.tags.split(',').map((item) => item.trim()).filter(Boolean),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo guardar la memoria');
      toast.success('Memoria operativa guardada');
      setMemoryForm({ title: '', category: 'aprendizaje', content: '', tags: '' });
      await loadMemory();
      await loadOverview();
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar la memoria');
    } finally {
      setBusy(false);
    }
  };

  const executionChart = overview?.execution_history || [];

  return (
    <div className="space-y-6" data-testid="agent-os-hub">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Sistema operativo de agentes</p>
          <h3 className="mt-2 text-2xl font-semibold text-text-heading">Orquestación interna Neuralia369</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-text-muted">MVP admin-first con aprobación humana para ventas, contenido, soporte e integraciones técnicas, inspirado en n8n + Claude Code pero 100% dentro del ecosistema.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={refreshAll} className="btn-secondary px-4 py-2 text-sm" data-testid="agent-os-refresh-button"><RefreshCw className="h-4 w-4" /> Refrescar</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="agent-os-section-tabs">
        {SECTION_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSection(tab.id)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${section === tab.id ? 'border-primary bg-primary text-background' : 'border-border-subtle bg-card text-text-body hover:border-primary/40'}`}
            data-testid={`agent-os-tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {section === 'summary' && overview && (
        <div className="space-y-6" data-testid="agent-os-summary-view">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={Layers3} label="Workflows activos" value={overview.metrics.active_workflows} helper={`${overview.metrics.total_workflows} totales`} testId="agent-os-metric-workflows" />
            <MetricCard icon={ShieldCheck} label="Aprobaciones pendientes" value={overview.metrics.pending_approvals} helper="Human-in-the-loop activo" testId="agent-os-metric-approvals" />
            <MetricCard icon={Cpu} label="Ejecuciones" value={overview.metrics.executions} helper={`Success rate ${overview.metrics.success_rate}%`} testId="agent-os-metric-executions" />
            <MetricCard icon={Database} label="Memoria operativa" value={overview.metrics.memory_entries} helper="Aprendizajes y contexto reutilizable" testId="agent-os-metric-memory" />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-[28px] border border-border-subtle bg-card/40 p-6" data-testid="agent-os-summary-chart-card">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pulso de ejecución</p>
                  <h4 className="mt-2 text-lg font-semibold text-text-heading">Histórico reciente</h4>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={executionChart}>
                  <defs>
                    <linearGradient id="agentOsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#00E5FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#00E5FF" fill="url(#agentOsGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-[28px] border border-border-subtle bg-card/40 p-6" data-testid="agent-os-recent-logs-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Trazabilidad</p>
              <h4 className="mt-2 text-lg font-semibold text-text-heading">Últimos logs</h4>
              <div className="mt-5 space-y-3">
                {(overview.recent_logs || []).map((log) => (
                  <div key={log.id} className="rounded-2xl border border-border-subtle bg-background/70 px-4 py-3" data-testid={`agent-os-log-${log.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-text-heading">{log.message}</p>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{log.level}</span>
                    </div>
                    <p className="mt-2 text-xs text-text-muted">{new Date(log.timestamp).toLocaleString('es-ES')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-[28px] border border-border-subtle bg-card/40 p-6" data-testid="agent-os-templates-grid">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Plantillas base</p>
                <h4 className="mt-2 text-lg font-semibold text-text-heading">Arranque rápido por caso de uso</h4>
              </div>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {(overview.templates || []).map((template) => (
                <div key={template.key} className="rounded-[24px] border border-border-subtle bg-background/70 p-5" data-testid={`agent-os-template-${template.key}`}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{template.category}</p>
                  <h5 className="mt-2 text-base font-semibold text-text-heading">{template.name}</h5>
                  <p className="mt-2 text-sm text-text-muted">{template.description}</p>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <p className="text-xs text-text-muted">{template.nodes_count} nodos · aprobación humana</p>
                    <button onClick={() => createFromTemplate(template)} className="btn-primary px-4 py-2 text-sm" disabled={busy} data-testid={`agent-os-create-template-${template.key}`}>
                      <Plus className="h-4 w-4" /> Crear workflow
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {section === 'workflows' && (
        <div className="grid gap-6 xl:grid-cols-[0.42fr_1fr]" data-testid="agent-os-workflows-view">
          <div className="space-y-4 rounded-[28px] border border-border-subtle bg-card/40 p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Biblioteca</p>
                <h4 className="mt-2 text-lg font-semibold text-text-heading">Workflows</h4>
              </div>
            </div>
            <div className="space-y-3 max-h-[780px] overflow-y-auto" data-testid="agent-os-workflow-list">
              {workflows.map((workflow) => (
                <button key={workflow.id} onClick={() => setSelectedWorkflowId(workflow.id)} className={`w-full rounded-[24px] border px-4 py-4 text-left transition-colors ${selectedWorkflowId === workflow.id ? 'border-primary bg-primary/10' : 'border-border-subtle bg-background/70 hover:border-primary/40'}`} data-testid={`agent-os-workflow-card-${workflow.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-text-heading">{workflow.name}</p>
                    <span className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-text-muted">{workflow.status}</span>
                  </div>
                  <p className="mt-2 text-xs text-text-muted">{workflow.description}</p>
                  <p className="mt-3 text-[11px] text-text-muted">{workflow.category} · {workflow.use_case}</p>
                </button>
              ))}
            </div>
          </div>

          {workflowDraft ? (
            <div className="space-y-6">
              <div className="rounded-[28px] border border-border-subtle bg-card/40 p-5">
                <div className="grid gap-4 xl:grid-cols-[1fr_0.72fr]">
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Nombre</label>
                      <input value={workflowDraft.name} onChange={(e) => updateWorkflowDraftField('name', e.target.value)} className="input-kinesthetic" data-testid="agent-os-workflow-name-input" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Descripción</label>
                      <textarea rows={3} value={workflowDraft.description} onChange={(e) => updateWorkflowDraftField('description', e.target.value)} className="input-kinesthetic resize-none" data-testid="agent-os-workflow-description-input" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Categoría</label>
                        <input value={workflowDraft.category} onChange={(e) => updateWorkflowDraftField('category', e.target.value)} className="input-kinesthetic" data-testid="agent-os-workflow-category-input" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Caso de uso</label>
                        <input value={workflowDraft.use_case} onChange={(e) => updateWorkflowDraftField('use_case', e.target.value)} className="input-kinesthetic" data-testid="agent-os-workflow-usecase-input" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado</label>
                        <select value={workflowDraft.status} onChange={(e) => updateWorkflowDraftField('status', e.target.value)} className="input-kinesthetic" data-testid="agent-os-workflow-status-select">
                          <option value="draft">draft</option>
                          <option value="active">active</option>
                          <option value="paused">paused</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 rounded-[24px] border border-border-subtle bg-background/70 p-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Modo actual</p>
                      <p className="mt-2 text-sm font-semibold text-text-heading inline-flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> aprobación humana</p>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Contexto de prueba</label>
                      <textarea rows={5} value={executionContext} onChange={(e) => setExecutionContext(e.target.value)} className="input-kinesthetic resize-none" placeholder="Ej: Lead frío de una agencia de Madrid interesada en automatización comercial y captación de citas." data-testid="agent-os-workflow-run-context" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={saveWorkflowDraft} className="btn-secondary px-4 py-2 text-sm" disabled={busy} data-testid="agent-os-workflow-save-button"><Save className="h-4 w-4" /> Guardar</button>
                      <button onClick={runWorkflow} className="btn-primary px-4 py-2 text-sm" disabled={busy} data-testid="agent-os-workflow-run-button"><Play className="h-4 w-4" /> Ejecutar</button>
                    </div>
                  </div>
                </div>
              </div>

              <WorkflowCanvas workflow={workflowDraft} selectedNodeId={selectedNodeId} onSelectNode={setSelectedNodeId} onWorkflowChange={setWorkflowDraft} />

              {selectedNodeId && workflowDraft?.nodes && (
                <div className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid="agent-os-node-editor-panel">
                  <h4 className="text-lg font-semibold text-text-heading">Edición rápida del nodo</h4>
                  <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Etiqueta</label>
                      <input value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.label || ''} onChange={(e) => updateSelectedNodeField('label', e.target.value)} className="input-kinesthetic" data-testid="agent-os-node-label-input" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Descripción</label>
                      <input value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.description || ''} onChange={(e) => updateSelectedNodeField('description', e.target.value)} className="input-kinesthetic" data-testid="agent-os-node-description-input" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Prompt</label>
                      <textarea rows={4} value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.config?.prompt || ''} onChange={(e) => updateSelectedNodeField('prompt', e.target.value)} className="input-kinesthetic resize-none" data-testid="agent-os-node-prompt-input" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Tool</label>
                        <input value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.config?.tool_key || ''} onChange={(e) => updateSelectedNodeField('tool_key', e.target.value)} className="input-kinesthetic" data-testid="agent-os-node-tool-input" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Rol</label>
                        <input value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.config?.agent_role || ''} onChange={(e) => updateSelectedNodeField('agent_role', e.target.value)} className="input-kinesthetic" data-testid="agent-os-node-role-input" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Acción</label>
                        <input value={workflowDraft.nodes.find((node) => node.id === selectedNodeId)?.config?.action || ''} onChange={(e) => updateSelectedNodeField('action', e.target.value)} className="input-kinesthetic" data-testid="agent-os-node-action-input" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-[28px] border border-dashed border-border-subtle bg-card/20 p-12 text-center text-text-muted" data-testid="agent-os-workflow-empty-state">Selecciona un workflow para abrir el constructor visual.</div>
          )}
        </div>
      )}

      {section === 'approvals' && (
        <div className="space-y-4" data-testid="agent-os-approvals-view">
          {approvals.map((approval) => (
            <div key={approval.id} className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid={`agent-os-approval-card-${approval.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{approval.workflow_name}</p>
                  <h4 className="mt-2 text-lg font-semibold text-text-heading">{approval.node_label}</h4>
                  <p className="mt-2 max-w-3xl whitespace-pre-wrap text-sm text-text-muted">{approval.preview}</p>
                </div>
                <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-300">pendiente</span>
              </div>
              <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_auto]">
                <textarea rows={3} value={approvalNotes[approval.id] || ''} onChange={(e) => setApprovalNotes((prev) => ({ ...prev, [approval.id]: e.target.value }))} className="input-kinesthetic resize-none" placeholder="Notas para aprobar o rechazar este paso" data-testid={`agent-os-approval-notes-${approval.id}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => resolveApproval(approval.id, 'approved')} className="btn-primary px-4 py-2 text-sm" disabled={busy} data-testid={`agent-os-approve-button-${approval.id}`}><CheckCircle2 className="h-4 w-4" /> Aprobar</button>
                  <button onClick={() => resolveApproval(approval.id, 'rejected')} className="btn-secondary px-4 py-2 text-sm" disabled={busy} data-testid={`agent-os-reject-button-${approval.id}`}><XCircle className="h-4 w-4" /> Rechazar</button>
                </div>
              </div>
            </div>
          ))}
          {approvals.length === 0 && <div className="rounded-[28px] border border-dashed border-border-subtle bg-card/20 p-12 text-center text-text-muted" data-testid="agent-os-approvals-empty">No hay aprobaciones pendientes ahora mismo.</div>}
        </div>
      )}

      {section === 'tools' && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="agent-os-tools-grid">
          {tools.map((tool) => {
            const Icon = TOOL_ICONS[tool.key] || Cpu;
            return (
              <div key={tool.key} className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid={`agent-os-tool-card-${tool.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border-subtle bg-background/70 text-primary"><Icon className="h-5 w-5" /></div>
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{tool.name}</p>
                      <p className="text-xs text-text-muted">{tool.scope}</p>
                    </div>
                  </div>
                  <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[tool.status] || STATUS_STYLES.pending}`} data-testid={`agent-os-tool-status-${tool.key}`}>{tool.status}</span>
                </div>
                <p className="mt-4 text-sm text-text-muted">{tool.description}</p>
              </div>
            );
          })}
        </div>
      )}

      {section === 'memory' && (
        <div className="grid gap-6 xl:grid-cols-[0.56fr_1fr]" data-testid="agent-os-memory-view">
          <form onSubmit={createMemoryEntry} className="rounded-[28px] border border-border-subtle bg-card/40 p-5 space-y-4" data-testid="agent-os-memory-form">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Título</label>
              <input value={memoryForm.title} onChange={(e) => setMemoryForm((prev) => ({ ...prev, title: e.target.value }))} className="input-kinesthetic" data-testid="agent-os-memory-title-input" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Categoría</label>
              <input value={memoryForm.category} onChange={(e) => setMemoryForm((prev) => ({ ...prev, category: e.target.value }))} className="input-kinesthetic" data-testid="agent-os-memory-category-input" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Contenido</label>
              <textarea rows={6} value={memoryForm.content} onChange={(e) => setMemoryForm((prev) => ({ ...prev, content: e.target.value }))} className="input-kinesthetic resize-none" data-testid="agent-os-memory-content-input" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Tags</label>
              <input value={memoryForm.tags} onChange={(e) => setMemoryForm((prev) => ({ ...prev, tags: e.target.value }))} className="input-kinesthetic" placeholder="ventas, contenido, soporte" data-testid="agent-os-memory-tags-input" />
            </div>
            <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="agent-os-memory-save-button"><Save className="h-4 w-4" /> Guardar memoria</button>
          </form>

          <div className="space-y-4" data-testid="agent-os-memory-list">
            {memoryEntries.map((entry) => (
              <div key={entry.id} className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid={`agent-os-memory-card-${entry.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{entry.category}</p>
                    <h4 className="mt-2 text-base font-semibold text-text-heading">{entry.title}</h4>
                  </div>
                  <p className="text-xs text-text-muted">{new Date(entry.updated_at).toLocaleDateString('es-ES')}</p>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm text-text-muted">{entry.content}</p>
                {!!entry.tags?.length && <div className="mt-4 flex flex-wrap gap-2">{entry.tags.map((tag) => <span key={tag} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-text-muted">{tag}</span>)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}